/**
 * カレンダーに重ねる追加データの取得
 *
 * 筋トレセッションと完了済みTODOは日付指定のAPIを持たないため、
 * 表示範囲ぶんをまとめて1回取りに行き、イベント生成側で日付に振り分ける。
 */

import { useQuery } from '@tanstack/react-query'

import { listTodos } from '@/api/services/todo'
import { getSessions } from '@/api/services/workout'
import type { TodoResponse } from '@/api/types/todo'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import { selectCanUseTodo, useAppStore } from '@/store/useAppStore'

/** 筋トレセッションの取得件数。1日1〜2件なので3か月ぶんに相当する */
const WORKOUT_SESSION_LIMIT = 200
/** 完了TODOの取得件数。未完了タスクも一緒に返るため多めに取る */
const TODO_LIMIT = 1000

const EMPTY_WORKOUTS: WorkoutSessionListItem[] = []
const EMPTY_TODOS: TodoResponse[] = []

type Range = {
  rangeStart: Date
  rangeEnd: Date
}

export const useCalendarExtras = ({ rangeStart, rangeEnd }: Range) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const canUseTodo = useAppStore(selectCanUseTodo)

  const { data: workouts } = useQuery({
    queryKey: ['activity-workout-sessions'],
    queryFn: () => getSessions(WORKOUT_SESSION_LIMIT),
    enabled: hasFullAccess,
  })

  const fromIso = rangeStart.toISOString()
  const toIso = rangeEnd.toISOString()

  const { data: todos } = useQuery({
    queryKey: ['activity-completed-todos', fromIso, toIso],
    queryFn: () => listTodos({ include_done: true, completed_from: fromIso, completed_to: toIso, limit: TODO_LIMIT }),
    enabled: canUseTodo,
  })

  return {
    workouts: workouts ?? EMPTY_WORKOUTS,
    todos: todos ?? EMPTY_TODOS,
  }
}
